export const RefundStatus = {
  Pending: 'pending',
  RequiresAction: 'requires_action',
  Succeeded: 'succeeded',
  Failed: 'failed',
  Canceled: 'canceled',
} as const

export type RefundStatus = typeof RefundStatus[keyof typeof RefundStatus]

/**
 * One refund as seen by a participant. `status: null` means unknown - never
 * treat it as completed. Only `succeeded` proves the money actually moved.
 */
export interface Refund {
  id: number
  amount: string
  currency: string
  status: RefundStatus | null
  /** Set only when `status` is `succeeded`. */
  refunded_at: string | null
  created_at: string | null
}

/** Which Startline flow created a refund. `null` on an organizer refund means unknown origin. */
export const RefundOrigin = {
  OrganizerRefund: 'organizer_refund',
  OrganizerCancel: 'organizer_cancel',
  ParticipantCancel: 'participant_cancel',
  RaceSwitch: 'race_switch',
  RaceSwitchCompletion: 'race_switch_completion',
} as const

export type RefundOrigin = typeof RefundOrigin[keyof typeof RefundOrigin]

/** Why a new refund cannot be issued on a registration right now. */
export const RefundBlockedReason = {
  NoOnlinePayment: 'no_online_payment',
  PaymentAccountUnavailable: 'payment_account_unavailable',
  RefundInProgress: 'refund_in_progress',
  FullyRefunded: 'fully_refunded',
} as const

export type RefundBlockedReason = typeof RefundBlockedReason[keyof typeof RefundBlockedReason]

export interface RefundInitiator {
  id: number
  first_name: string
  last_name: string
}

/**
 * One refund as seen by an organizer. Adds origin, the organisation-internal
 * reason and the initiator. Never carries a provider reference.
 */
export interface OrganizerRefund extends Refund {
  origin: RefundOrigin | null
  /** Organisation-internal free text. Never shown to the participant. */
  reason: string | null
  initiated_by: RefundInitiator | null
}

export interface RefundTotals {
  /** Sum of completed (succeeded) refunds. "0.00" when none. */
  refunded_amount: string
  /** Sum of refunds still in flight (pending, requires_action, unknown). "0.00" when none. */
  refund_pending_amount: string
}

/**
 * A refund that Stripe rejected at creation time (issue #215). The action that
 * triggered it (cancel, delete, switch) still took effect; this object only
 * reports that the participant was NOT refunded. Served in `meta.refund_failure`.
 */
export interface RefundFailure {
  /** Decimal string: the amount Startline tried to refund, e.g. "25.00". */
  amount: string
  /** Lowercase ISO-4217 code, e.g. "eur". */
  currency: string
  /** Localised, audience-specific sentence. Primary text to show. */
  message: string
}
